/**
 * Shared category -> subcategory -> sub-subcategory taxonomy helpers.
 *
 * This centralises the same cascading category logic used by the luxury /
 * pre-owned listing flow so other listing forms (e.g. the designer shop offer
 * flow) can reuse the exact same drop-down behaviour instead of a flat list.
 */
import { PRE_OWNED_ITEM_CATEGORY_DATA } from './preOwnedItemCategories';
import {
  isClothingListingCategory,
  joinClothingSubcategoryPath,
} from './clothingCategorySchema';
import {
  isWatchesListingCategory,
  joinWatchSubcategoryPath,
} from './watchesCategorySchema';
import {
  isBagsListingCategory,
  joinBagSubcategoryPath,
} from './bagsCategorySchema';
import {
  isFootwearListingCategory,
  joinFootwearSubcategoryPath,
} from './footwearCategorySchema';
import {
  isAccessoriesListingCategory,
  joinAccessorySubcategoryPath,
} from './accessoriesCategorySchema';
import {
  KITCHEN_DINING_ROOT,
  isHomeKitchenListingCategory,
  isKitchenDiningSubcategory,
  joinKitchenDiningSubcategoryPath,
} from './kitchenDiningCategorySchema';

/** Every taxonomy-related field the cascade dropdowns can populate. */
export const EMPTY_CATEGORY_TAXONOMY = {
  category: '',
  subcategory: '',
  watch_department: '',
  watch_subcategory: '',
  bag_department: '',
  bag_subcategory: '',
  footwear_department: '',
  footwear_subcategory: '',
  accessory_department: '',
  accessory_subcategory: '',
  clothing_audience: '',
  clothing_type: '',
  clothing_subtype: '',
  clothing_style: '',
  kitchen_group: '',
  kitchen_type: '',
};

/** Dependent fields cleared whenever the top-level category changes. */
export const getTaxonomyResetPatch = () => {
  const { category: _category, ...rest } = EMPTY_CATEGORY_TAXONOMY;
  return { ...rest };
};

/** Generic (single-level) subcategories for categories without a structured tree. */
export function getGenericSubcategories(category) {
  return PRE_OWNED_ITEM_CATEGORY_DATA[category] || [];
}

/** True when a category exposes structured department/sub dropdowns. */
export function hasStructuredTaxonomy(category) {
  return (
    isWatchesListingCategory(category) ||
    isBagsListingCategory(category) ||
    isFootwearListingCategory(category) ||
    isAccessoriesListingCategory(category) ||
    isClothingListingCategory(category)
  );
}

/**
 * Resolve the final joined `subcategory` string from the taxonomy selections,
 * mirroring the luxury/pre-owned listing submit logic.
 */
export function buildTaxonomySubcategory(t = {}) {
  const category = t.category;
  if (!category) return '';

  if (isWatchesListingCategory(category)) {
    return joinWatchSubcategoryPath([t.watch_department, t.watch_subcategory]);
  }
  if (isBagsListingCategory(category)) {
    return joinBagSubcategoryPath([t.bag_department, t.bag_subcategory]);
  }
  if (isFootwearListingCategory(category)) {
    return joinFootwearSubcategoryPath([t.footwear_department, t.footwear_subcategory]);
  }
  if (isAccessoriesListingCategory(category)) {
    return joinAccessorySubcategoryPath([t.accessory_department, t.accessory_subcategory]);
  }
  if (isClothingListingCategory(category)) {
    return joinClothingSubcategoryPath([
      t.clothing_audience,
      t.clothing_type,
      t.clothing_subtype,
      t.clothing_style,
    ]);
  }
  if (isHomeKitchenListingCategory(category) && isKitchenDiningSubcategory(t.subcategory)) {
    return joinKitchenDiningSubcategoryPath([
      KITCHEN_DINING_ROOT,
      t.kitchen_group,
      t.kitchen_type,
    ]);
  }
  return t.subcategory || '';
}

/**
 * Validate the current taxonomy selections. Returns an errors object keyed by
 * the same field names the cascade component renders.
 */
export function validateCategoryTaxonomy(t = {}) {
  const errors = {};
  const category = t.category;

  if (!category) {
    errors.category = 'Please select a category';
    return errors;
  }

  if (isWatchesListingCategory(category)) {
    if (!t.watch_department) errors.watch_department = 'Select a department';
    else if (!t.watch_subcategory) errors.watch_subcategory = 'Select a subcategory';
  } else if (isBagsListingCategory(category)) {
    if (!t.bag_department) errors.bag_department = 'Select a department';
    else if (!t.bag_subcategory) errors.bag_subcategory = 'Select a subcategory';
  } else if (isFootwearListingCategory(category)) {
    if (!t.footwear_department) errors.footwear_department = 'Select a department';
    else if (!t.footwear_subcategory) errors.footwear_subcategory = 'Select a subcategory';
  } else if (isAccessoriesListingCategory(category)) {
    if (!t.accessory_department) errors.accessory_department = 'Select a department';
    else if (!t.accessory_subcategory) errors.accessory_subcategory = 'Select a subcategory';
  } else if (isClothingListingCategory(category)) {
    if (!t.clothing_audience) errors.clothing_audience = 'Select a department';
    else if (!t.clothing_type) errors.clothing_type = 'Select a clothing type';
    else if (!t.clothing_subtype) errors.clothing_subtype = 'Select a subtype';
    else if (!t.clothing_style) errors.clothing_style = 'Select a style';
  } else if (isHomeKitchenListingCategory(category)) {
    if (getGenericSubcategories(category).length > 0 && !t.subcategory) {
      errors.subcategory = 'Please select a subcategory';
    } else if (isKitchenDiningSubcategory(t.subcategory)) {
      if (!t.kitchen_group) errors.kitchen_group = 'Select kitchen category';
      else if (!t.kitchen_type) errors.kitchen_type = 'Select kitchen type';
    }
  } else if (getGenericSubcategories(category).length > 0 && !t.subcategory) {
    errors.subcategory = 'Please select a subcategory';
  }

  return errors;
}
